import React from "react"
import PropTypes from "prop-types"
import {
    PlayArrowRounded,
    ScheduleOutlined,
    OndemandVideoOutlined,
    LockOutlined,
    AttachFileOutlined,
    CheckCircle
} from "@mui/icons-material"
import { resolveVideoSource } from "./VideoPlayer"
import { plural } from "./datetime"

/**
 * One lecture in a course's syllabus. `preview` marks a free preview lecture
 * that a student can watch before enrolling.
 */
export default function LectureCard({
    lecture,
    index,
    active,
    locked = false,
    preview = false,
    onSelect
}) {
    const { kind } = resolveVideoSource(lecture.video_url)
    const completed = Boolean(lecture.progress?.completed)

    return (
        <button
            type="button"
            onClick={() => onSelect(lecture)}
            className={`flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition sm:gap-4 sm:p-5 ${
                active
                    ? "border-indigo-400 bg-indigo-50/60 shadow-sm"
                    : "border-slate-200 bg-white hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-md"
            }`}
        >
            <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold ${
                    active
                        ? "bg-indigo-600 text-white"
                        : "bg-slate-100 text-slate-500"
                }`}
            >
                {locked ? (
                    <LockOutlined sx={{ fontSize: 18 }} />
                ) : completed ? (
                    <CheckCircle sx={{ fontSize: 20, color: active ? "#fff" : "#059669" }} />
                ) : typeof index === "number" ? (
                    index + 1
                ) : (
                    <PlayArrowRounded />
                )}
            </span>

            <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-slate-800 sm:text-base">
                        {lecture.title}
                    </span>
                    {preview && (
                        <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                            Preview
                        </span>
                    )}
                </span>

                {lecture.description && (
                    <span className="mt-1 line-clamp-2 block text-xs text-slate-500 sm:text-sm">
                        {lecture.description}
                    </span>
                )}

                <span className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                    {lecture.duration && (
                        <span className="flex items-center gap-1">
                            <ScheduleOutlined sx={{ fontSize: 14 }} />
                            {lecture.duration}
                        </span>
                    )}
                    <span className="flex items-center gap-1">
                        {locked ? (
                            <>
                                <LockOutlined sx={{ fontSize: 14 }} />
                                Locked
                            </>
                        ) : (
                            <>
                                <OndemandVideoOutlined sx={{ fontSize: 14 }} />
                                {kind === "embed" ? "Streaming" : "Video"}
                            </>
                        )}
                    </span>
                    {lecture.resource_count > 0 && (
                        <span className="flex items-center gap-1">
                            <AttachFileOutlined sx={{ fontSize: 14 }} />
                            {plural(lecture.resource_count, "resource")}
                        </span>
                    )}
                </span>
            </span>
        </button>
    )
}

LectureCard.propTypes = {
    lecture: PropTypes.object.isRequired,
    index: PropTypes.number,
    active: PropTypes.bool,
    locked: PropTypes.bool,
    preview: PropTypes.bool,
    onSelect: PropTypes.func.isRequired
}
